import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { match } from "ts-pattern";

import {
  CreateOrganizationDocument,
  type CreateOrganizationMutation,
  type CreateOrganizationMutationVariables,
  GetOrganizationsDocument,
} from "~/features/admin/organizations/gqls";
import type { OrganizationFormData } from "~/features/admin/organizations/types";
import { translateError } from "~/lib/errors";

interface UseOrganizationCreateOptions {
  onSuccess?: () => void;
}

// t("model.organization.modelName") — resolved indirectly by createdSuccessfully's nested $t().
/** Create an organization. */
export function useOrganizationCreate({ onSuccess }: UseOrganizationCreateOptions = {}) {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [createOrganization] = useMutation<CreateOrganizationMutation, CreateOrganizationMutationVariables>(
    CreateOrganizationDocument,
    { refetchQueries: [GetOrganizationsDocument] },
  );

  const submit = useCallback(
    async (formData: OrganizationFormData) => {
      setProcessing(true);
      try {
        const res = await createOrganization({ variables: { input: formData } });
        match(res.data?.cnutCreateOrganization)
          .with({ __typename: "ValidationError" }, (e) => toast.error(e.fields.map((f) => f.message).join(", ")))
          .with({ __typename: "OrganizationCreateOutput" }, () => {
            toast.success(t("feature.common.createdSuccessfully", { model: "organization" }));
            onSuccess?.();
          })
          .otherwise(() => {});
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [createOrganization, t, onSuccess],
  );

  return { submit, processing };
}
